import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AlertCircle, Smartphone, FileText, UserX, Database, Sparkles } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const primaryColor = "#43bd98";
const CARD_GAP = 16;

const PainPoints = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 640px)");
	const trackRef = useRef(null);
	const [active, setActive] = useState(0);

	// El paso de scroll es el ancho real de una tarjeta, que en móvil es un
	// porcentaje del viewport y no un valor fijo
	const stepWidth = () => {
		const track = trackRef.current;
		const first = track?.firstElementChild;
		return first ? first.offsetWidth + CARD_GAP : 0;
	};

	const handleScroll = useCallback(() => {
		const track = trackRef.current;
		const step = stepWidth();
		if (!track || !step) return;
		setActive(Math.round(track.scrollLeft / step));
	}, []);

	const goTo = (index) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({ left: index * stepWidth(), behavior: "smooth" });
	};

	const painPoints = [
		{
			icon: <Smartphone size={24} strokeWidth={2} />,
			title: t("restaurantPainPoints.items.noApp.title"),
			text: t("restaurantPainPoints.items.noApp.text"),
		},
		{
			icon: <FileText size={24} strokeWidth={2} />,
			title: t("restaurantPainPoints.items.noMenu.title"),
			text: t("restaurantPainPoints.items.noMenu.text"),
		},
		{
			icon: <UserX size={24} strokeWidth={2} />,
			title: t("restaurantPainPoints.items.noLoyalty.title"),
			text: t("restaurantPainPoints.items.noLoyalty.text"),
		},
		{
			icon: <Database size={24} strokeWidth={2} />,
			title: t("restaurantPainPoints.items.noControl.title"),
			text: t("restaurantPainPoints.items.noControl.text"),
		},
	];

	return (
		<section
			style={{
				padding: "5rem 2rem",
				background: "#fff",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div style={{ maxWidth: "1000px", margin: "0 auto" }}>
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "3rem" }}
				>
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
							background: "#fef2f2",
							color: "#dc2626",
							padding: "8px 16px",
							borderRadius: "24px",
							fontSize: "0.9rem",
							fontWeight: 600,
							marginBottom: "1rem",
						}}
					>
						<AlertCircle size={18} />
						{t("restaurantPainPoints.badge")}
					</div>

					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
							fontWeight: 800,
							color: "#1a1a1a",
							lineHeight: 1.3,
							margin: 0,
						}}
					>
						{t("restaurantPainPoints.title")}
					</h2>

					<p
						style={{
							fontSize: "1.1rem",
							color: "#6b7280",
							marginTop: "1rem",
							maxWidth: "600px",
							marginInline: "auto",
						}}
					>
						{t("restaurantPainPoints.subtitle")}
					</p>
				</motion.div>

				{/* En escritorio caben las cuatro en rejilla; en móvil se convierten
				    en un carrusel para no encadenar cuatro tarjetas altas */}
				<div
					ref={trackRef}
					onScroll={isMobile ? handleScroll : undefined}
					className={isMobile ? "hide-scrollbar" : undefined}
					style={
						isMobile
							? {
									display: "flex",
									gap: `${CARD_GAP}px`,
									overflowX: "auto",
									scrollSnapType: "x mandatory",
									// Sangra hasta los bordes para que la tarjeta
									// siguiente asome, pero la primera siga alineada
									// con el texto de la sección
									marginInline: "-2rem",
									paddingInline: "2rem",
									scrollPaddingInline: "2rem",
									WebkitOverflowScrolling: "touch",
							  }
							: {
									display: "grid",
									gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
									gap: "1.5rem",
							  }
					}
				>
					{painPoints.map((item, index) => (
						<motion.div
							key={index}
							initial={{ opacity: 0, y: 20 }}
							// En el carrusel las tarjetas fuera de pantalla nunca
							// entrarían en viewport, así que allí se animan al montar
							animate={isMobile ? { opacity: 1, y: 0 } : undefined}
							whileInView={isMobile ? undefined : { opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: index * 0.1 }}
							viewport={{ once: true }}
							style={{
								background: "#fafafa",
								border: "1px solid #e5e7eb",
								borderRadius: "16px",
								padding: "1.5rem",
								textAlign: "center",
								...(isMobile && {
									flex: "0 0 78%",
									scrollSnapAlign: "start",
								}),
							}}
						>
							<div
								style={{
									width: "50px",
									height: "50px",
									borderRadius: "12px",
									background: "#fee2e2",
									color: "#dc2626",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									margin: "0 auto 1rem",
								}}
							>
								{item.icon}
							</div>
							<h3
								style={{
									fontSize: "1.05rem",
									fontWeight: 700,
									color: "#1f2937",
									marginBottom: "0.5rem",
								}}
							>
								{item.title}
							</h3>
							<p
								style={{
									fontSize: "0.9rem",
									color: "#6b7280",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								{item.text}
							</p>
						</motion.div>
					))}
				</div>

				{isMobile && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: "8px",
							marginTop: "1.25rem",
						}}
					>
						{painPoints.map((item, index) => (
							<button
								key={index}
								type="button"
								onClick={() => goTo(index)}
								aria-label={item.title}
								aria-current={index === active}
								style={{
									// El área táctil es de 24px aunque el punto se vea de 8
									width: "24px",
									height: "24px",
									padding: 0,
									border: "none",
									background: "none",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<span
									style={{
										display: "block",
										width: index === active ? "24px" : "8px",
										height: "8px",
										borderRadius: "4px",
										background: index === active ? "#dc2626" : "#e5e7eb",
										transition: "width 0.25s ease, background 0.25s ease",
									}}
								/>
							</button>
						))}
					</div>
				)}

				{/* Highlight box - Solution teaser */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.3 }}
					viewport={{ once: true }}
					style={{
						marginTop: "3rem",
						background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
						border: `2px solid ${primaryColor}30`,
						borderRadius: "20px",
						padding: "2rem",
						textAlign: "center",
					}}
				>
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: "50px",
							height: "50px",
							borderRadius: "12px",
							background: primaryColor,
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						<Sparkles size={24} />
					</div>

					<h3
						style={{
							fontSize: "1.3rem",
							fontWeight: 700,
							color: "#1a1a1a",
							marginBottom: "0.5rem",
						}}
					>
						{t("restaurantPainPoints.highlight.title")}
					</h3>

					<p
						style={{
							fontSize: "1rem",
							color: "#374151",
							maxWidth: "500px",
							margin: "0 auto",
							marginBottom: "1rem",
						}}
					>
						{t("restaurantPainPoints.highlight.text")}
					</p>

					<p
						style={{
							fontSize: "0.85rem",
							color: primaryColor,
							fontWeight: 600,
							margin: 0,
						}}
					>
						{t("restaurantPainPoints.highlight.note")}
					</p>
				</motion.div>
			</div>
		</section>
	);
};

export default PainPoints;
